/**
 * useBulkResolutionSelection — the resolution engine for the AI Actions list.
 *
 * Owns the `approvingTier`, `pendingApprove`, `pendingDismiss` selection
 * state machine and the cross-group selection, plus the approve/dismiss
 * execution (`executeBulkResolution`) that ties the cache
 * (useBulkResolutionCache) and navigation (useBulkResolutionCompletion)
 * concerns together. Extracted from useBulkResolution alongside those two
 * hooks so the subtle cache choreography (see executeBulkResolution's
 * comments) is reviewable in isolation.
 */
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { createProposalClient } from '@/lib/proposals/core/ProposalClient';
import type { ProposalListItem } from '@/lib/proposals/core/types';

import type { ApproveGroupRisk } from '../utils/approveGroupRisk';
import { detectApproveGroupRisk } from '../utils/approveGroupRisk';
import type {
  PriorityGroup,
  PriorityTier,
} from '../utils/groupProposalsByTier';
import type { PendingBulk } from './types';
import { useBulkResolutionCache } from './useBulkResolutionCache';
import { useBulkResolutionCompletion } from './useBulkResolutionCompletion';

interface UseBulkResolutionSelectionArgs {
  orgId: number | undefined;
  /** The pending list response — the selection resolves against it. */
  results: ProposalListItem[] | undefined;
}

export function useBulkResolutionSelection({
  orgId,
  results,
}: UseBulkResolutionSelectionArgs) {
  const {
    beginOptimisticRemoval,
    restoreFailedItems,
    markListsStaleWithoutRefetch,
    endOptimisticRemoval,
  } = useBulkResolutionCache(orgId);
  const { advanceToNext } = useBulkResolutionCompletion();

  const [approvingTier, setApprovingTier] = useState<string | null>(null);
  // Track selected proposal IDs for bulk approve
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // State for risk-gated approval confirmation dialog
  const [pendingApprove, setPendingApprove] = useState<PendingBulk | null>(
    null
  );
  // State for the dismiss confirmation dialog (always gated by a modal)
  const [pendingDismiss, setPendingDismiss] = useState<PendingBulk | null>(
    null
  );

  // Selection semantics (shift-click ranges, anchors) live in GroupedList.
  // The panel keeps numeric ids for the bulk operations; GroupedList speaks
  // string ids — these two adapters convert at the boundary.
  const selectedIdStrings = useMemo(
    () => new Set([...selectedIds].map(String)),
    [selectedIds]
  );
  const handleSelectionChange = useCallback((next: Set<string>) => {
    setSelectedIds(new Set([...next].map(Number)));
  }, []);
  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Approve OR dismiss a resolved set of proposals (a whole priority group,
  // or the cross-group selection — the caller decides the scope up front).
  // Both operations share identical optimistic-removal, partial-failure
  // restore, navigation, and cache-invalidation logic — only the per-proposal
  // client call and the user-facing verb differ.
  const executeBulkResolution = useCallback(
    async (
      tier: PriorityTier | 'selected',
      actionsToResolve: ProposalListItem[],
      mode: 'approve' | 'dismiss',
      /** Id of the action to navigate to after resolution. Pass only for
          single-action resolutions — bulk resolutions leave the list in place.
          Must be captured BEFORE the await so cache mutations during the await
          don't cause a stale lookup. */
      nextActionId?: number | null
    ) => {
      if (approvingTier !== null) {
        return;
      } // Prevent double-clicks (shared guard across approve + dismiss)

      const op =
        mode === 'approve'
          ? {
              run: (
                client: ReturnType<typeof createProposalClient>,
                id: number
              ) => client.approve(id),
              verbPast: 'Approved',
            }
          : {
              run: (
                client: ReturnType<typeof createProposalClient>,
                id: number
              ) => client.reject(id),
              verbPast: 'Dismissed',
            };

      const toApprove = actionsToResolve;
      if (toApprove.length === 0) {
        return;
      }

      if (!orgId) {
        return;
      }

      setApprovingTier(tier);

      const idsToRemove = new Set(toApprove.map(a => a.id));

      beginOptimisticRemoval(idsToRemove);

      // Clear removed items from selection immediately
      setSelectedIds(prev => {
        const next = new Set(prev);
        for (const id of idsToRemove) {
          next.delete(id);
        }
        return next;
      });

      try {
        // Call the API client directly instead of going through the mutation
        // hook. Each mutation's onSuccess triggers invalidateQueries on the
        // list, causing a refetch that overwrites our optimistic removal with
        // server data that may still contain not-yet-processed items. By
        // calling the client directly we skip automatic cache invalidation
        // and do a single invalidation at the end.
        const client = createProposalClient(orgId);

        const settled = await Promise.allSettled(
          toApprove.map(proposal => op.run(client, proposal.id))
        );

        let succeeded = 0;
        let failed = 0;
        const failedItems: ProposalListItem[] = [];

        settled.forEach((result, idx) => {
          if (result.status === 'fulfilled') {
            succeeded++;
          } else {
            console.error(
              '[actions] Failed to approve proposal in bulk operation',
              { proposalId: toApprove[idx].id, orgId },
              result.reason
            );
            failed++;
            failedItems.push(toApprove[idx]);
          }
        });

        // Re-add failed items back to the list
        if (failedItems.length > 0) {
          restoreFailedItems(failedItems);

          console.warn('[actions] Bulk resolution partial failure', {
            mode,
            succeeded,
            failed,
            tier,
            orgId,
          });
        }

        if (failed === 0) {
          toast.success(
            `${op.verbPast} ${succeeded} action${succeeded === 1 ? '' : 's'}`
          );
        } else {
          toast.error(
            `${op.verbPast} ${succeeded}, ${failed} couldn't be completed.`
          );
        }

        markListsStaleWithoutRefetch();

        // Single-action advance: only on full success — if the single
        // resolve failed the item is restored to the list and the user
        // should retry. The index panel's single-row paths intentionally
        // never pass nextActionId (resolving a row keeps the user on the
        // list); this stays wired for callers that legitimately advance.
        if (failed === 0) {
          advanceToNext(orgId, nextActionId);
        }
      } catch (e) {
        console.error(
          '[actions] Catastrophic failure in executeBulkResolution',
          { mode, tier, orgId },
          e
        );
        // Mark list caches stale WITHOUT refetching — same race as the
        // success path above.
        markListsStaleWithoutRefetch();
        const verb = mode === 'approve' ? 'approval' : 'dismissal';
        toast.error(`We couldn't complete the ${verb}. Try again.`);
      } finally {
        endOptimisticRemoval(idsToRemove);
        setApprovingTier(null);
      }
    },
    [
      orgId,
      approvingTier,
      beginOptimisticRemoval,
      restoreFailedItems,
      markListsStaleWithoutRefetch,
      endOptimisticRemoval,
      advanceToNext,
    ]
  );

  // The cross-group selection, as list items (for the SelectionToolbar ops)
  const selectedProposals = useMemo(
    () => results?.filter(p => selectedIds.has(p.id)) ?? [],
    [results, selectedIds]
  );

  // Gate approval through a confirmation dialog when risky tags are present
  const requestApproveGroup = useCallback(
    (group: PriorityGroup) => {
      if (approvingTier !== null || group.actions.length === 0) {
        return;
      }
      const risk = detectApproveGroupRisk(group.actions);
      if (!risk.hasRisk) {
        void executeBulkResolution(group.tier, group.actions, 'approve');
        return;
      }
      setPendingApprove({
        tier: group.tier,
        actions: group.actions,
        buttonLabel: 'Approve all',
      });
    },
    [approvingTier, executeBulkResolution]
  );

  // Dismiss (reject) always opens a confirmation modal before resolving
  const requestDismissGroup = useCallback(
    (group: PriorityGroup) => {
      if (approvingTier !== null || group.actions.length === 0) {
        return;
      }
      setPendingDismiss({
        tier: group.tier,
        actions: group.actions,
        buttonLabel: 'Dismiss all',
      });
    },
    [approvingTier]
  );

  // Single-row hover actions. Index single-row resolves keep the user on the
  // list — advancing to the next action is exclusively the full-page
  // footer's behavior. Approve is risk-gated exactly like the bulk path: a
  // row whose approval sends an email/calendar invite or deletes a CRM
  // record confirms first — one accidental hover-click must never send an
  // email. Dismiss executes immediately (it's recoverable — the AI can
  // suggest the action again).
  const requestApproveSingle = useCallback(
    (action: ProposalListItem) => {
      if (approvingTier !== null) {
        return;
      }
      const risk = detectApproveGroupRisk([action]);
      if (!risk.hasRisk) {
        void executeBulkResolution('selected', [action], 'approve');
        return;
      }
      setPendingApprove({
        tier: 'selected',
        actions: [action],
        buttonLabel: 'Approve',
      });
    },
    [approvingTier, executeBulkResolution]
  );

  const requestDismissSingle = useCallback(
    (action: ProposalListItem) => {
      if (approvingTier !== null) {
        return;
      }
      void executeBulkResolution('selected', [action], 'dismiss');
    },
    [approvingTier, executeBulkResolution]
  );

  // Selection-toolbar counterparts: operate on the cross-group selection.
  const requestApproveSelected = useCallback(() => {
    if (approvingTier !== null || selectedProposals.length === 0) {
      return;
    }
    const risk = detectApproveGroupRisk(selectedProposals);
    if (!risk.hasRisk) {
      void executeBulkResolution('selected', selectedProposals, 'approve');
      return;
    }
    setPendingApprove({
      tier: 'selected',
      actions: selectedProposals,
      buttonLabel: 'Approve selected',
    });
  }, [approvingTier, selectedProposals, executeBulkResolution]);

  const requestDismissSelected = useCallback(() => {
    if (approvingTier !== null || selectedProposals.length === 0) {
      return;
    }
    setPendingDismiss({
      tier: 'selected',
      actions: selectedProposals,
      buttonLabel: 'Dismiss selected',
    });
  }, [approvingTier, selectedProposals]);

  // Derived modal-display values — the pending state already carries its
  // resolved scope, so no re-deriving from selectedIds here.
  const pendingRisk = useMemo(
    (): ApproveGroupRisk | null =>
      pendingApprove ? detectApproveGroupRisk(pendingApprove.actions) : null,
    [pendingApprove]
  );

  const pendingCount = pendingApprove?.actions.length ?? 0;

  const handleConfirmApprove = useCallback(async () => {
    if (!pendingApprove) {
      return;
    }
    const pending = pendingApprove;
    setPendingApprove(null);
    await executeBulkResolution(pending.tier, pending.actions, 'approve');
  }, [pendingApprove, executeBulkResolution]);

  const handleCancelApprove = useCallback(() => {
    setPendingApprove(null);
  }, []);

  const dismissCount = pendingDismiss?.actions.length ?? 0;

  const handleConfirmDismiss = useCallback(async () => {
    if (!pendingDismiss) {
      return;
    }
    const pending = pendingDismiss;
    setPendingDismiss(null);
    await executeBulkResolution(pending.tier, pending.actions, 'dismiss');
  }, [pendingDismiss, executeBulkResolution]);

  const handleCancelDismiss = useCallback(() => {
    setPendingDismiss(null);
  }, []);

  return {
    approvingTier,
    selectedIds,
    selectedIdStrings,
    handleSelectionChange,
    handleClearSelection,
    requestApproveGroup,
    requestDismissGroup,
    requestApproveSingle,
    requestDismissSingle,
    requestApproveSelected,
    requestDismissSelected,
    pendingApprove,
    pendingRisk,
    pendingCount,
    handleConfirmApprove,
    handleCancelApprove,
    pendingDismiss,
    dismissCount,
    handleConfirmDismiss,
    handleCancelDismiss,
  };
}
